import { useEffect, useRef, useState, useMemo } from 'react';
import ForceGraph2D from 'react-force-graph-2d';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import {
  Search,
  Maximize2,
  Minimize2,
  ZoomIn,
  ZoomOut,
  RotateCcw,
  Download,
  X
} from 'lucide-react';
import type { DatabaseState, Study, Compound, Mushroom, Condition } from '@/types/database';
import { NodeDetailPanel } from './NodeDetailPanel';

interface GraphNode {
  id: string;
  name: string;
  type: 'study' | 'compound' | 'mushroom' | 'condition';
  val?: number; // node size
  color?: string;
  data?: any; // original data object
}

interface GraphLink {
  source: string;
  target: string;
  type: string;
  value?: number; // link strength
}

interface KnowledgeGraphData {
  nodes: GraphNode[];
  links: GraphLink[];
}

interface CompoundKnowledgeGraphProps {
  data: DatabaseState;
  height?: number;
  onNodeClick?: (node: GraphNode) => void;
  highlightedPath?: string[];
}

const NODE_COLORS = {
  study: '#A98A2C',      // Gold
  compound: '#C26A1E',   // Orange
  mushroom: '#B98B36',   // Warm gold
  condition: '#B45F3D'   // Terracotta
};

const NODE_SIZES = {
  study: 8,
  compound: 12,
  mushroom: 15,
  condition: 10
};

export function CompoundKnowledgeGraph({
  data,
  height = 800,
  onNodeClick,
  highlightedPath = []
}: CompoundKnowledgeGraphProps) {
  const graphRef = useRef<any>();
  const [graphData, setGraphData] = useState<KnowledgeGraphData>({ nodes: [], links: [] });
  const [searchTerm, setSearchTerm] = useState('');
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedNode, setSelectedNode] = useState<GraphNode | null>(null);
  const [exploreNodeId, setExploreNodeId] = useState<string | null>(null);

  // Create set of highlighted nodes and links
  const highlightedNodes = useMemo(() => new Set(highlightedPath), [highlightedPath]);
  const highlightedLinks = useMemo(() => {
    const links = new Set<string>();
    for (let i = 0; i < highlightedPath.length - 1; i++) {
      links.add(`${highlightedPath[i]}-${highlightedPath[i + 1]}`);
      links.add(`${highlightedPath[i + 1]}-${highlightedPath[i]}`); // bidirectional
    }
    return links;
  }, [highlightedPath]);

  // Filter toggles
  const [showStudies, setShowStudies] = useState(true);
  const [showCompounds, setShowCompounds] = useState(true);
  const [showMushrooms, setShowMushrooms] = useState(true);
  const [showConditions, setShowConditions] = useState(true);

  // Transform database into graph data
  const transformData = useMemo(() => {
    const nodes: GraphNode[] = [];
    const links: GraphLink[] = [];
    const nodeMap = new Map<string, GraphNode>();

    // Add compound nodes
    if (showCompounds && data.compounds) {
      data.compounds.forEach((compound: Compound) => {
        const node: GraphNode = {
          id: compound.compound_id,
          name: compound.name,
          type: 'compound',
          val: NODE_SIZES.compound,
          color: NODE_COLORS.compound,
          data: compound
        };
        nodes.push(node);
        nodeMap.set(compound.compound_id, node);
      });
    }

    // Add mushroom nodes
    if (showMushrooms && data.mushrooms) {
      data.mushrooms.forEach((mushroom: Mushroom) => {
        const node: GraphNode = {
          id: mushroom.mushroom_id,
          name: mushroom.common_name,
          type: 'mushroom',
          val: NODE_SIZES.mushroom,
          color: NODE_COLORS.mushroom,
          data: mushroom
        };
        nodes.push(node);
        nodeMap.set(mushroom.mushroom_id, node);
      });
    }

    // Add condition nodes
    if (showConditions && data.conditions) {
      data.conditions.forEach((condition: Condition) => {
        const node: GraphNode = {
          id: condition.condition_id,
          name: condition.name,
          type: 'condition',
          val: NODE_SIZES.condition,
          color: NODE_COLORS.condition,
          data: condition
        };
        nodes.push(node);
        nodeMap.set(condition.condition_id, node);
      });
    }

    // Add study nodes
    if (showStudies && data.studies) {
      data.studies.forEach((study: Study) => {
        const node: GraphNode = {
          id: study.study_id,
          name: study.title.substring(0, 50) + '...',
          type: 'study',
          val: NODE_SIZES.study,
          color: NODE_COLORS.study,
          data: study
        };
        nodes.push(node);
        nodeMap.set(study.study_id, node);
      });
    }

    // Create links: Studies ↔ Compounds
    if (data.study_compounds) {
      data.study_compounds.forEach((sc) => {
        if (nodeMap.has(sc.study_id) && nodeMap.has(sc.compound_id)) {
          links.push({
            source: sc.study_id,
            target: sc.compound_id,
            type: 'contains_compound',
            value: sc.is_primary ? 2 : 1
          });
        }
      });
    }

    // Create links: Studies ↔ Mushrooms (via interventions)
    if (data.interventions) {
      data.interventions.forEach((intervention) => {
        if (nodeMap.has(intervention.study_id) && nodeMap.has(intervention.mushroom_id)) {
          links.push({
            source: intervention.study_id,
            target: intervention.mushroom_id,
            type: 'studies_mushroom',
            value: 1
          });
        }
      });
    }

    // Create links: Studies ↔ Conditions
    if (data.study_conditions) {
      data.study_conditions.forEach((sc) => {
        if (nodeMap.has(sc.study_id) && nodeMap.has(sc.condition_id)) {
          links.push({
            source: sc.study_id,
            target: sc.condition_id,
            type: 'treats_condition',
            value: sc.is_primary ? 2 : 1
          });
        }
      });
    }

    return { nodes, links };
  }, [data, showStudies, showCompounds, showMushrooms, showConditions]);

  // Filter by search term or explore connections
  const filteredGraphData = useMemo(() => {
    let baseData = transformData;

    // If exploring connections, filter to connected nodes only
    if (exploreNodeId) {
      const connectedNodeIds = new Set<string>([exploreNodeId]);

      // Find all directly connected nodes
      transformData.links.forEach(link => {
        if (String(link.source) === exploreNodeId) {
          connectedNodeIds.add(String(link.target));
        }
        if (String(link.target) === exploreNodeId) {
          connectedNodeIds.add(String(link.source));
        }
      });

      const filteredNodes = transformData.nodes.filter(node =>
        connectedNodeIds.has(node.id)
      );

      const filteredLinks = transformData.links.filter(link =>
        connectedNodeIds.has(String(link.source)) && connectedNodeIds.has(String(link.target))
      );

      baseData = { nodes: filteredNodes, links: filteredLinks };
    }

    // Then apply search filter if present
    if (!searchTerm) return baseData;

    const searchLower = searchTerm.toLowerCase();
    const matchingNodes = baseData.nodes.filter(node =>
      node.name.toLowerCase().includes(searchLower)
    );
    const matchingNodeIds = new Set(matchingNodes.map(n => n.id));

    // Include nodes that are connected to matching nodes
    const connectedNodeIds = new Set<string>();
    baseData.links.forEach(link => {
      if (matchingNodeIds.has(String(link.source)) || matchingNodeIds.has(String(link.target))) {
        connectedNodeIds.add(String(link.source));
        connectedNodeIds.add(String(link.target));
      }
    });

    const filteredNodes = baseData.nodes.filter(node =>
      connectedNodeIds.has(node.id)
    );

    const filteredLinks = baseData.links.filter(link =>
      connectedNodeIds.has(String(link.source)) && connectedNodeIds.has(String(link.target))
    );

    return { nodes: filteredNodes, links: filteredLinks };
  }, [transformData, searchTerm, exploreNodeId]);

  useEffect(() => {
    setGraphData(filteredGraphData);
  }, [filteredGraphData]);

  const handleNodeClick = (node: GraphNode) => {
    setSelectedNode(node);
    if (onNodeClick) {
      onNodeClick(node);
    }
  };

  const handleZoomIn = () => {
    if (graphRef.current) {
      graphRef.current.zoom(graphRef.current.zoom() * 1.2, 400);
    }
  };

  const handleZoomOut = () => {
    if (graphRef.current) {
      graphRef.current.zoom(graphRef.current.zoom() * 0.8, 400);
    }
  };

  const handleReset = () => {
    if (graphRef.current) {
      graphRef.current.zoomToFit(400);
    }
  };

  const handleDownload = () => {
    // Export graph as image
    const canvas = document.querySelector('canvas');
    if (canvas) {
      const url = canvas.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = 'fungalchemy-knowledge-graph.png';
      link.href = url;
      link.click();
    }
  };

  const handleExploreConnections = (nodeId: string) => {
    setExploreNodeId(nodeId);
    // Re-fit graph after filtering
    setTimeout(() => {
      if (graphRef.current) {
        graphRef.current.zoomToFit(400);
      }
    }, 100);
  };

  const handleClearExplore = () => {
    setExploreNodeId(null);
    setTimeout(() => {
      if (graphRef.current) {
        graphRef.current.zoomToFit(400);
      }
    }, 100);
  };

  return (
    <div className={`relative ${isFullscreen ? 'fixed inset-0 z-50 bg-background' : ''}`}>
      <Card className="overflow-hidden">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Compound Knowledge Graph</CardTitle>
              <CardDescription>
                Obsidian-style visualization of {graphData.nodes.length} nodes and {graphData.links.length} relationships
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={handleZoomIn}
                title="Zoom In"
              >
                <ZoomIn className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={handleZoomOut}
                title="Zoom Out"
              >
                <ZoomOut className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={handleReset}
                title="Reset View"
              >
                <RotateCcw className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={handleDownload}
                title="Download Image"
              >
                <Download className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="icon"
                onClick={() => setIsFullscreen(!isFullscreen)}
                title={isFullscreen ? 'Exit Fullscreen' : 'Fullscreen'}
              >
                {isFullscreen ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {/* Controls */}
          <div className="mb-4 space-y-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search compounds, studies, mushrooms, or conditions..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>

            {/* Explore Active Indicator */}
            {exploreNodeId && (
              <div className="flex items-center justify-between rounded-lg border border-[#C8A53D] bg-[#F4F2EC] dark:bg-[#0D0D0D] p-3">
                <div className="flex items-center gap-2">
                  <Badge variant="default">Exploring Connections</Badge>
                  <span className="text-sm text-muted-foreground">
                    Showing only nodes connected to selected node
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleClearExplore}
                  className="h-8"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear
                </Button>
              </div>
            )}

            {/* Filters */}
            <div className="flex flex-wrap gap-4">
              <div className="flex items-center space-x-2">
                <Switch
                  id="show-studies"
                  checked={showStudies}
                  onCheckedChange={setShowStudies}
                />
                <Label htmlFor="show-studies" className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full" style={{ backgroundColor: NODE_COLORS.study }} />
                  Studies ({data.studies?.length || 0})
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="show-compounds"
                  checked={showCompounds}
                  onCheckedChange={setShowCompounds}
                />
                <Label htmlFor="show-compounds" className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full" style={{ backgroundColor: NODE_COLORS.compound }} />
                  Compounds ({data.compounds?.length || 0})
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="show-mushrooms"
                  checked={showMushrooms}
                  onCheckedChange={setShowMushrooms}
                />
                <Label htmlFor="show-mushrooms" className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full" style={{ backgroundColor: NODE_COLORS.mushroom }} />
                  Mushrooms ({data.mushrooms?.length || 0})
                </Label>
              </div>
              <div className="flex items-center space-x-2">
                <Switch
                  id="show-conditions"
                  checked={showConditions}
                  onCheckedChange={setShowConditions}
                />
                <Label htmlFor="show-conditions" className="flex items-center gap-2">
                  <span className="h-3 w-3 rounded-full" style={{ backgroundColor: NODE_COLORS.condition }} />
                  Conditions ({data.conditions?.length || 0})
                </Label>
              </div>
            </div>
          </div>

          {/* Graph Visualization */}
          <div className="relative rounded-lg border bg-[#0D0D0D]" style={{ height: isFullscreen ? 'calc(100vh - 250px)' : height }}>
            <ForceGraph2D
              ref={graphRef}
              graphData={graphData}
              backgroundColor="#0D0D0D"
              nodeLabel={(node) => (node as GraphNode).name}
              nodeColor={(node) => {
                const n = node as GraphNode;
                if (highlightedNodes.has(n.id)) {
                  return '#C8A53D'; // Bright gold for highlighted nodes
                }
                return n.color || '#6E6B64';
              }}
              nodeVal={(node) => {
                const n = node as GraphNode;
                if (highlightedNodes.has(n.id)) {
                  return (n.val || 5) * 1.5; // Larger for highlighted nodes
                }
                return n.val || 5;
              }}
              nodeCanvasObject={(node, ctx, globalScale) => {
                const n = node as GraphNode;
                const label = n.name;
                const fontSize = 12 / globalScale;
                ctx.font = `${fontSize}px Sans-Serif`;

                // Draw node circle
                ctx.beginPath();
                ctx.arc(node.x!, node.y!, n.val || 5, 0, 2 * Math.PI, false);
                ctx.fillStyle = n.color || '#6E6B64';
                ctx.fill();

                // Draw label for large nodes or selected node
                if ((n.val && n.val > 10) || selectedNode?.id === n.id) {
                  const textWidth = ctx.measureText(label).width;
                  const bckgDimensions = [textWidth, fontSize].map(n => n + fontSize * 0.2);

                  ctx.fillStyle = 'rgba(0, 0, 0, 0.8)';
                  ctx.fillRect(
                    node.x! - bckgDimensions[0] / 2,
                    node.y! + (n.val || 5) + 2,
                    bckgDimensions[0],
                    bckgDimensions[1]
                  );

                  ctx.textAlign = 'center';
                  ctx.textBaseline = 'middle';
                  ctx.fillStyle = '#FAFAF8';
                  ctx.fillText(label, node.x!, node.y! + (n.val || 5) + 2 + fontSize / 2);
                }
              }}
              linkColor={(link) => {
                const l = link as any;
                const linkKey = `${l.source.id || l.source}-${l.target.id || l.target}`;
                if (highlightedLinks.has(linkKey)) {
                  return '#C8A53D'; // Bright gold for highlighted links
                }
                return 'rgba(255, 255, 255, 0.2)';
              }}
              linkWidth={(link) => {
                const l = link as any;
                const linkKey = `${l.source.id || l.source}-${l.target.id || l.target}`;
                if (highlightedLinks.has(linkKey)) {
                  return 3; // Thicker for highlighted links
                }
                return (link as GraphLink).value || 1;
              }}
              linkDirectionalParticles={(link) => {
                const l = link as any;
                const linkKey = `${l.source.id || l.source}-${l.target.id || l.target}`;
                if (highlightedLinks.has(linkKey)) {
                  return 6; // More particles for highlighted path
                }
                return 2;
              }}
              linkDirectionalParticleSpeed={(link) => {
                const l = link as any;
                const linkKey = `${l.source.id || l.source}-${l.target.id || l.target}`;
                if (highlightedLinks.has(linkKey)) {
                  return 0.01; // Faster particles for highlighted path
                }
                return 0.005;
              }}
              linkDirectionalParticleColor={(link) => {
                const l = link as any;
                const linkKey = `${l.source.id || l.source}-${l.target.id || l.target}`;
                if (highlightedLinks.has(linkKey)) {
                  return '#C8A53D'; // Bright gold particles
                }
                return 'rgba(255, 255, 255, 0.5)';
              }}
              onNodeClick={(node) => handleNodeClick(node as GraphNode)}
              cooldownTicks={100}
              onEngineStop={() => {
                if (graphRef.current) {
                  graphRef.current.zoomToFit(400);
                }
              }}
            />
          </div>

        </CardContent>
      </Card>

      {/* Node Detail Panel */}
      <NodeDetailPanel
        node={selectedNode}
        database={data}
        onClose={() => setSelectedNode(null)}
        onExploreConnections={handleExploreConnections}
      />
    </div>
  );
}
